import { bool, func, object, shape } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { renderCheck } from 'ravenjs/utils/viewport';

import List from 'ravenjs/lib/List';
import ListItem from 'ravenjs/lib/ListItem';
import Radio from 'ravenjs/lib/Radio';
import Row from 'ravenjs/lib/Row';
import TextArea from 'ravenjs/lib/TextArea';
import Typography from 'ravenjs/lib/Typography';

import { FEEDBACK_OPTIONS, FEEDBACK_QUESTION, FEEDBACK_MAX_CHARACTERS } from 'constants/ai';
import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';

import Modal from '../Modal';

class FeedbackModal extends Component {
    static propTypes = {
        closeModal: func.isRequired,
        modal: shape({
            data: object,
            open: bool,
        }),
    };

    static defaultProps = {
        modal: {
            open: false,
            data: null,
        },
    };

    constructor(props) {
        super(props);

        this.state = {
            feedbackAnswer: null,
            feedbackMessage: '',
            colorMaxChar: DEFAULT_COLORS.GREY_20,
            submitFeedback: false,
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown = event => {
        if (event.key === 'Escape') {
            this.handleCloseModal();
        }
    };

    getModalProps = () => {
        const { modal } = this.props;
        const ModalProps = get(modal, 'data.ModalProps', {});
        const actions = get(modal, 'data.actions', []);
        const cancelBtnProps = get(modal, 'data.cancelBtnProps');
        const onCancel = get(modal, 'data.onCancel');
        const onSuccess = get(modal, 'data.onSuccess');
        const open = get(modal, 'open', false);
        const successBtnProps = get(modal, 'data.successBtnProps');
        const maxWidth = get(modal, 'data.maxWidth', 950);
        const justifyActions = get(modal, 'data.justifyActions', 'flex-end');
        const isMobile = renderCheck('md', 'less');

        return {
            actions: this.getModalActions(),
            backDropProps: {
                zIndex: 100,
                userSelect: 'auto',
            },
            cancelBtnProps,
            content: this.renderContent(),
            hideActions: false,
            id: MODALS.FEEDBACK_MODAL,
            justifyActions,
            onCancel,
            onSuccess,
            open,
            successBtnProps,
            ModalProps: {
                ContentProps: {
                    style: {
                        maxWidth,
                        margin: '0',
                        borderRadius: '1rem',
                    },
                },
                onBackdropClick: () => {
                    this.handleCloseModal();
                },
                ...ModalProps,
            },
            modalBodyProps: {
                padding: '0',
            },
            modalFooterProps: {
                display: 'flex',
                justifyContent: actions.length > 1 ? 'space-between' : 'center',
                padding: isMobile ? '0.5rem 1.125rem' : '1rem 0 0 0',
            },
        };
    };

    clearFeedback = () => {
        this.setState({
            feedbackAnswer: null,
            feedbackMessage: '',
            colorMaxChar: DEFAULT_COLORS.GREY_20,
            submitFeedback: false,
        });
    };

    getModalActions = () => {
        const { closeModal, modal } = this.props;
        const { feedbackAnswer, feedbackMessage, submitFeedback } = this.state;
        const handlers = get(modal, 'data.handlers', {});
        const itemData = get(modal, 'data.itemData', {});
        const positionChat = get(modal, 'data.positionChat', 0);

        const actions = [
            {
                text: MESSAGES.MODAL.ACTIONS.CANCEL,
                color: 'secondary',
                padding: '0.844rem 1.5rem',
                style: {
                    fontSize: '1rem',
                    fontFamily: 'Favorit',
                    borderRadius: '2px',
                    marginRight: '26px',
                    width: '50%',
                },
                onClick: () => {
                    this.handleCloseModal();
                },
            },
            {
                acl: submitFeedback,
                text: MESSAGES.MODAL.ACTIONS.SUBMIT_FEEDBACK,
                color: 'primary',
                padding: '0.844rem 1.5rem',
                style: {
                    fontSize: '1rem',
                    fontFamily: 'Favorit',
                    borderRadius: '2px',
                    width: '50%',
                },
                onClick: () => {
                    closeModal(MODALS.FEEDBACK_MODAL);
                    this.clearFeedback();
                    const submitFeedback = getHandler(handlers, 'submitFeedback');
                    callFunc(
                        submitFeedback,
                        {
                            feedbackAnswer,
                            feedbackMessage,
                            isHelpful: false,
                            ...itemData,
                        },
                        positionChat
                    );
                },
            },
        ];

        return actions;
    };

    handleChange = e => {
        const { value } = e.target;
        if (value && value.length <= FEEDBACK_MAX_CHARACTERS) {
            this.setState({
                feedbackMessage: value,
                colorMaxChar: DEFAULT_COLORS.GREY_20,
            });
        } else if (isEmpty(value)) {
            this.setState({
                feedbackMessage: value,
                colorMaxChar: DEFAULT_COLORS.GREY_20,
            });
        } else {
            this.setState({
                colorMaxChar: DEFAULT_COLORS.RED_2,
            });
        }
    };

    handleCloseModal = () => {
        const { closeModal, modal } = this.props;
        const handlers = get(modal, 'data.handlers', {});
        closeModal(MODALS.FEEDBACK_MODAL);
        this.clearFeedback();
        const positionChat = get(modal, 'data.positionChat', 0);
        const handleCancelButton = getHandler(handlers, 'handleCancelButton');
        callFunc(handleCancelButton, positionChat);
    };

    renderContent = () => {
        const { modal } = this.props;
        const { feedbackAnswer, feedbackMessage, colorMaxChar } = this.state;
        const title = get(modal, 'data.title', '');
        const textAreaStyle = {
            color: colorMaxChar === DEFAULT_COLORS.RED_2 ? DEFAULT_COLORS.RED_2 : undefined,
            borderColor: DEFAULT_COLORS.BLACK,
            borderRadius: '0.125rem',
        };

        return (
            <>
                <Typography type="h4" gutterTop="0" gutterBottom="1rem">
                    {title}
                </Typography>
                <Row id="containerFeedback" direction="column" margin="0">
                    <Typography type="pSmallBody" gutterTop="0" style={{ marginBottom: '0.5rem' }}>
                        {FEEDBACK_QUESTION}
                    </Typography>
                    <List margin="0 0 1rem 0">
                        {FEEDBACK_OPTIONS.map((option, index) => {
                            const { id, label, value } = option;
                            const checked = feedbackAnswer === label;
                            const onChange = () => {
                                this.setState({
                                    feedbackAnswer: label,
                                    submitFeedback: true,
                                });
                            };

                            return (
                                <ListItem
                                    key={id}
                                    padding="0.375rem 0"
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    <Radio
                                        checked={checked}
                                        id={value}
                                        className="check-item"
                                        name={value}
                                        onChange={onChange}
                                        required
                                        value={value}
                                    />
                                    <Typography
                                        type="pSmallBody"
                                        gutterTop="0"
                                        gutterLeft="0.25rem"
                                        onClick={onChange}
                                        style={{
                                            cursor: 'pointer',
                                            marginBottom: '0',
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>
                    <TextArea
                        height="10rem"
                        padding="0.875rem"
                        value={feedbackMessage}
                        onChange={this.handleChange}
                        style={textAreaStyle}
                    />
                    <Typography
                        type="pSmallBody"
                        color={colorMaxChar}
                        style={{ marginTop: '0.25rem' }}
                    >
                        {feedbackMessage.length}/{FEEDBACK_MAX_CHARACTERS}
                    </Typography>
                </Row>
            </>
        );
    };

    render() {
        return <Modal {...this.getModalProps()} />;
    }
}

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.FEEDBACK_MODAL),
});

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
};

export { FeedbackModal as FeedbackModalUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(FeedbackModal);
